import { Dispatch, SetStateAction } from "react"

import Icon from "@/components/Icon"
import { Button } from "@/components/ui/button"
import { FilterProperty, FilterType } from "@/utils/filtering"

import FilterFormRow, { FilterSelectState } from "./FilterFormRow"

interface FilterFormProps {
  selectsStates: FilterSelectState[]
  setSelectsStates: Dispatch<SetStateAction<FilterSelectState[]>>
  onApply: (filters: FilterSelectState[]) => void
  properties: FilterProperty[]
  fixedColumns?: boolean
}

const FilterForm = ({
  selectsStates,
  setSelectsStates,
  onApply,
  properties,
  fixedColumns = false,
}: FilterFormProps) => {
  const updateSelectState = ({
    index,
    id,
    filterType,
    value,
  }: {
    id?: string
    filterType?: FilterType
    value?: string | string[]
    index: number
  }) => {
    setSelectsStates((prevState) => {
      const newSelectsStates = [...prevState]
      newSelectsStates[index] = {
        id: id || newSelectsStates[index].id,
        filterType: filterType || newSelectsStates[index].filterType,
        value: value !== undefined ? value : id ? "" : newSelectsStates[index].value,
      }
      return newSelectsStates
    })
  }

  const addSelect = () => {
    setSelectsStates((prevState) => [
      ...prevState,
      { id: "", filterType: FilterType.Is, value: "" },
    ])
  }

  const removeSelect = (index: number) => {
    setSelectsStates((prevState) => {
      const next = [...prevState]
      next.splice(index, 1)
      return [...next]
    })
  }

  const resetFilter = () => {
    setSelectsStates([{ id: "", filterType: FilterType.Is, value: "" }])
    onApply([])
  }

  const applyFilter = () => {
    const validFilters = selectsStates.filter(({ id, value }) => id !== "" && value.length > 0)
    onApply(validFilters)
  }

  return (
    <div className="mt-2 flex flex-col gap-y-2">
      {selectsStates.map((selectState, index) => (
        <div
          key={`filter-select-${index}`}
          className={`flex items-center gap-x-3 ${index === 0 ? "pr-9" : ""}`}
        >
          <FilterFormRow
            selectState={selectState}
            updateSelectState={({ id, filterType, value }) =>
              updateSelectState({ index, id, filterType, value })
            }
            properties={properties}
            fixedColumns={fixedColumns}
          />
          {index !== 0 && (
            <button onClick={() => removeSelect(index)} type="button">
              <Icon name="minusCircle" className="h-6 w-6" />
            </button>
          )}
        </div>
      ))}
      <div className="mt-3 flex justify-between">
        <button onClick={addSelect} type="button">
          <Icon name="plusCircle" className="h-6 w-6" />
        </button>
        <div className="flex justify-end gap-x-2">
          <Button onClick={resetFilter} type="button" variant="ghost">
            Clear all
          </Button>
          <Button onClick={applyFilter} type="button">
            Apply filter
          </Button>
        </div>
      </div>
    </div>
  )
}

export default FilterForm
